import React from 'react';
import {
  CompletePhoneNumberVerificationAndSignInMutation,
  DocumentKey,
  SelfOnboardingAbortReason,
  SelfOnboardingWorkerAction,
  useCompletePhoneNumberVerificationAndSignInMutation,
  useStartPhoneNumberVerificationMutation,
  useUpsertJobsiteWorkerDocumentMutation,
} from 'apollo/generated/client-operations';
import { getGraphQLError, hasGraphQLErrorMessage } from 'utils/error';
import { AuthContext } from 'auth';
import { AlertService } from 'components/alertService';
import { Timer } from 'components/timer';
import { CodeInput } from 'components/codeInput';
import { isDocumentVisible } from 'containers/selfOnboarding/helpers/utils';
import {
  SelfOnboardingStepProps,
  SelfOnboardingStepKey,
} from 'containers/selfOnboarding/steps/types';
import { Copy } from 'containers/selfOnboarding/steps/utils';
import { getWorkerConsentDocumentInput } from 'containers/selfOnboarding/steps/basicInfoStep/BasicInfoStep.forms';
import { getPhoneNumberAsE164 } from 'utils';

type VerificationResult =
  CompletePhoneNumberVerificationAndSignInMutation['completePhoneNumberVerificationAndSignIn'];

// `auth` is required: the handler only runs once a worker is known to have an
// Odin account.
type SignInVerifiedWorkerArgs = {
  auth: NonNullable<VerificationResult['auth']>;
  jobsiteWorker: VerificationResult['jobsiteWorker'];
  selfOnboardingInfo: VerificationResult['selfOnboardingInfo'];
};

/**
 * Legacy self-onboarding OTP completion: verifies an existing worker's phone and
 * signs them in. Reachable only on the flag-OFF (legacy) path — the
 * authenticated flow uses its own forked login-complete step wired to the
 * accountless OTP mutation. [sc-16468]
 */
export function LoginCompleteStep(
  props: SelfOnboardingStepProps,
): React.ReactElement {
  const {
    state,
    navigation,
    localize,
    language,
    jobsiteInvitation,
    refetchData,
  } = props;
  const { verificationPhoneNumber, contractorId } = state;
  const { jobsite, contractor } = jobsiteInvitation?.jobsiteContractor ?? {};
  const { jobsiteId } = jobsite ?? {};
  const { goToStep } = navigation;

  const { authState } = React.useContext(AuthContext);
  const [isSaving, setIsSaving] = React.useState(false);
  const [timerResetKey, setTimerResetKey] = React.useState(0);
  const [codeResetKey, setCodeResetKey] = React.useState(0);

  const resetTimer = (): void => setTimerResetKey((key) => key + 1);
  const resetCode = (): void => setCodeResetKey((key) => key + 1);

  const [completePhoneNumberVerificationAndSignIn] =
    useCompletePhoneNumberVerificationAndSignInMutation();
  const [startPhoneNumberVerification] =
    useStartPhoneNumberVerificationMutation();
  const [upsertJobsiteWorkerDocument] =
    useUpsertJobsiteWorkerDocumentMutation();

  const { modules } = jobsiteInvitation?.jobsiteContractor.jobsite ?? {};
  const isWorkerConsentAvailable = isDocumentVisible(
    DocumentKey.WorkerConsentDocument,
    modules,
  );

  // Sign the verified worker in, upsert worker consent, fast-forward via refetch,
  // and route to a terminal step on abort.
  //
  // jobsiteWorker is NULL when the jobsite uses the default contractor, is not
  // configured with enforceValidContractorInSelfOnboarding, and the worker is
  // already on the jobsite through another contractor — in that case
  // continueAction is AbortOnboarding with reason
  // JobsiteWorkerWithDifferentContractor.
  const signInVerifiedWorker = async ({
    auth,
    jobsiteWorker,
    selfOnboardingInfo,
  }: SignInVerifiedWorkerArgs): Promise<void> => {
    authState.signIn(auth.session.sessionToken, null);

    if (isWorkerConsentAvailable && jobsiteWorker) {
      const documentInput = await getWorkerConsentDocumentInput({
        user: auth.user,
        jobsiteInvitation,
        state,
        jobsiteWorkerId: jobsiteWorker.jobsiteWorkerId,
      });
      if (documentInput) {
        await upsertJobsiteWorkerDocument({
          variables: { input: documentInput },
        });
      }
    }

    // signIn resets selfOnboardingCurrentStep server-side; refetch fast-forwards
    // the worker to it.
    await refetchData();

    const { continueAction, abortReason } = selfOnboardingInfo ?? {};
    if (continueAction === SelfOnboardingWorkerAction.AbortOnboarding) {
      goToStep(
        abortReason ===
          SelfOnboardingAbortReason.JobsiteWorkerWithDifferentContractor
          ? SelfOnboardingStepKey.JobsiteWorkerWithDifferentContractor
          : SelfOnboardingStepKey.AlreadyCompleted,
      );
    }
  };

  const onSubmit = async (verificationCode: string): Promise<void> => {
    if (isSaving) {
      return;
    }
    setIsSaving(true);

    try {
      const { data: resultData } =
        await completePhoneNumberVerificationAndSignIn({
          variables: {
            input: {
              verificationCode,
              jobsiteId,
              contractorId: contractorId?.value ?? contractor?.contractorId,
            },
          },
        });
      const { success, auth, jobsiteWorker, selfOnboardingInfo } =
        resultData.completePhoneNumberVerificationAndSignIn ?? {};
      setIsSaving(false);

      if (success) {
        await signInVerifiedWorker({ auth, jobsiteWorker, selfOnboardingInfo });
      }
    } catch (error) {
      setIsSaving(false);
      if (
        hasGraphQLErrorMessage(
          error,
          'Unexpected error value: { statusCode: 404 }',
        )
      ) {
        AlertService.alert('danger', 'Something went wrong!', 'Invalid code');
      } else {
        AlertService.alert(
          'danger',
          'Something went wrong!',
          getGraphQLError(error),
        );
      }
    }
  };

  const requestAnotherCode = async (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ): Promise<void> => {
    e.preventDefault();

    try {
      const e164PhoneNumber = getPhoneNumberAsE164(verificationPhoneNumber);
      const result = await startPhoneNumberVerification({
        variables: {
          input: { isLogin: true, phoneNumber: e164PhoneNumber },
        },
      });
      if (result.data.startPhoneNumberVerification.success) {
        resetTimer();
        resetCode();
      }
    } catch (error) {
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    }
  };

  return (
    <div>
      <h2 className="odin-text-1.5xl odin-text-odin-black odin-mt-7.5 odin-mb-6 odin-text-center">
        {localize(Copy.self_onboarding_confirm_phone_title)}
      </h2>
      <CodeInput
        onCodeFilled={onSubmit}
        onInvalidCodeFormat={(code: string): void => {
          AlertService.alert('danger', 'Invalid code format!', `"${code}"`);
        }}
        resetKey={codeResetKey}
      />
      <div className="odin-pt-10">
        <Timer
          language={language}
          textCopy={Copy.self_onboarding_confirm_phone_time_to_complete}
          onTimeout={(): void => goToStep(SelfOnboardingStepKey.Login)}
          resetKey={timerResetKey}
        />
      </div>
      <div className="odin-pt-6.5 odin-px-5 odin-text-sm odin-text-center">
        {localize(Copy.self_onboarding_confirm_phone_instruction)}{' '}
        <a href="" onClick={requestAnotherCode}>
          {localize(Copy.self_onboarding_confirm_phone_request_another_code)}
        </a>
      </div>
    </div>
  );
}
